/**
 * Presentation Layer - DataPreviewGrid Component
 * データプレビューグリッドコンポーネント
 */

import React from 'react';
import { Box, Alert } from '@mui/material';
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import type { DataDisplayMode } from '../hook/useDataPreview';

/**
 * Props定義
 */
export interface DataPreviewGridProps {
  /** 表示データ（行） */
  rows: GridRowsProp;
  /** カラム定義 */
  columns: GridColDef[];
  /** 表示モード */
  displayMode: DataDisplayMode;
  /** フィールド編集時のコールバック */
  onEditField?: (rowIndex: number, field: string) => void;
  /** ローディング中フラグ */
  isLoading?: boolean;
  /** 高さ（デフォルト: 400） */
  height?: number | string;
  /** 翌日指定モードフラグ */
  isNextDayMode?: boolean;
  /** 除外されたクライアントデータ件数 */
  excludedClientCount?: number;
}

/**
 * データプレビューグリッドコンポーネント
 */
export const DataPreviewGrid: React.FC<DataPreviewGridProps> = ({
  rows,
  columns,
  displayMode,
  onEditField,
  isLoading = false,
  height = 400,
  isNextDayMode = false,
  excludedClientCount = 0,
}) => {
  return (
    <Box sx={{ height, width: '100%' }}>
      {/* クライアントデータ除外通知 */}
      {isNextDayMode && excludedClientCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          クライアントデータ {excludedClientCount} 件を除外
        </Alert>
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        disableRowSelectionOnClick
        rowHeight={55}
        columnHeaderHeight={44}
        pageSizeOptions={[5, 10, 25, 100]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 100 },
          },
        }}
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          '& .MuiDataGrid-cell': { fontSize: 11 },
          // 加工済みモード時のスタイル
          ...(displayMode === 'processed' && {
            '& .MuiDataGrid-cell[data-field="ステータス"]': {
              fontWeight: 'medium',
            },
            '& .MuiDataGrid-cell[data-field="担当医師"]': {
              fontWeight: 'medium',
            },
          }),
        }}
        getRowClassName={(params) => {
          // 加工済みデータの場合、変更されたセルをハイライト
          if (displayMode === 'processed') {
            const row = params.row as Record<string, unknown>;
            if (row._isStatusConverted || row._isDoctorChanged) {
              return 'row-modified';
            }
          }
          return '';
        }}
      />
    </Box>
  );
};
